import { useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from '@/hooks/use-toast';

import { contextFormSchema, ContextFormData } from './shared/schema';
import { contextsService } from '@/services/contexts';
import { CreateContextData } from '@/types/contexts';
import One from './steps/One';
import Two from './steps/Two';

interface ContextWizardProps {
  open: boolean;
  onOpenChange: (_open: boolean) => void;
  onSuccess: () => void;
}

export default function ContextWizard({ open, onOpenChange, onSuccess }: ContextWizardProps) {
  const [currentPhase, setCurrentPhase] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const methods = useForm<ContextFormData>({
    mode: 'onChange',
    resolver: zodResolver(contextFormSchema),
    defaultValues: {
      name: '',
      contextId: '',
      type_id: '',
      associatedTopic: [],
      associatedTerritory: [],
      fixedKeywords: [],
      dynamicKeywords: [],
      startDate: new Date(),
      endDate: new Date(),
      frequency: '',
      dataOrigin: '',
      region: '',
      predominantLanguage: '',
      usualHashtags: [],
      mainWebsite: '',
      description: '',
      interestSectors: '',
    }
  });

  const handleNext = async () => {
    const fieldsToValidate = getFieldsForPhase(currentPhase);
    const isValid = await methods.trigger(fieldsToValidate);

    if (currentPhase < 2 && isValid) {
      setCurrentPhase((prev: number) => prev + 1);
    }
  };

  const getFieldsForPhase = (phase: number): (keyof ContextFormData)[] => {
    switch (phase) {
      case 1:
        return ['name', 'contextId', 'type_id', 'associatedTopic', 'associatedTerritory', 'fixedKeywords', 'dynamicKeywords', 'startDate', 'endDate', 'frequency', 'dataOrigin'];
      case 2:
        return ['region', 'predominantLanguage', 'usualHashtags', 'mainWebsite', 'description', 'interestSectors'];
      default:
        return [];
    }
  };

  const handleBack = () => {
    if (currentPhase > 1) {
      setCurrentPhase((prev: number) => prev - 1);
    }
  };

  const handleSubmit = async (data: any) => {
    setIsSubmitting(true);
    
    try {
      const createData: CreateContextData = {
        name: data.name,
        type_id: data.type_id,
        language_id: data.predominantLanguage,
        frequency: data.frequency,
        start_date: data.startDate.toISOString().split('T')[0],
        end_date: data.endDate.toISOString().split('T')[0],
        region: data.region,
        fixed_keywords: data.fixedKeywords,
        dynamic_keywords: data.dynamicKeywords,
        hashtags: data.usualHashtags,
        areas: [data.interestSectors],
        topics: data.associatedTopic,
        territories: data.associatedTerritory,
      };

      await contextsService.create(createData);
      
      toast({
        title: 'Sucesso',
        description: 'Contexto cadastrado com sucesso!',
      });
      
      onSuccess();
      onOpenChange(false);
      methods.reset();
      setCurrentPhase(1);
      
    } catch (error) {
      console.error('Erro ao cadastrar contexto:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao cadastrar contexto. Tente novamente.',
        variant: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderCurrentPhase = () => {
    switch (currentPhase) {
      case 1:
        return <One onNext={handleNext} />;
      case 2:
        return <Two onSubmit={methods.handleSubmit(handleSubmit)} onBack={handleBack} isSubmitting={isSubmitting} />;
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="pb-4">
          <DialogTitle className="text-lg font-semibold">
            Cadastro de Contexto - Fase {currentPhase} de 2
          </DialogTitle>
        </DialogHeader>
        <FormProvider {...methods}>
          <div className="space-y-4">
            {renderCurrentPhase()}
          </div>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}